import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Table from '../../components/UI/Table';
import Modal from '../../components/UI/Modal';
import { usersAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Shield, 
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Eye,
  CheckCircle,
  XCircle
} from 'lucide-react';
import Tooltip from '../../components/UI/Tooltip';

const UserManagementContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterCard = styled(Card)`
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 200px;
`;

const FilterLabel = styled.label`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  font-size: ${props => props.theme.typography.fontSize.base};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary + '20'};
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  
  ${props => {
    switch (props.status) {
      case 'active':
        return `
          background-color: ${props.theme.colors.success + '20'};
          color: ${props.theme.colors.success};
        `;
      case 'inactive':
        return `
          background-color: ${props.theme.colors.error + '20'};
          color: ${props.theme.colors.error};
        `;
      default:
        return `
          background-color: ${props.theme.colors.gray[100]};
          color: ${props.theme.colors.text.secondary};
        `;
    }
  }}
`;

const RoleBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  background-color: ${props => props.theme.colors.primary + '20'};
  color: ${props => props.theme.colors.primary};
`;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: ''
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const initialFormState = {
    first_name: '',
    last_name: '',
    email: '',
    role: '',
    is_active: true,
  };
  const [addForm, setAddForm] = useState(initialFormState);
  
  const { theme } = useTheme();
  const { user, hasPermission } = useAuth();

  const roleOptions = [
    { value: 'system_admin', label: 'System Administrator' },
    { value: 'regional_coordinator', label: 'Regional Coordinator' },
    { value: 'data_entry_officer', label: 'Data Entry Officer' },
    { value: 'program_officer', label: 'Program Officer' },
    { value: 'member', label: 'Member' },
    { value: 'super_admin', label: 'Super Administrator' },
  ];
  
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await usersAPI.getAll(filters);
        const payload = response.data;
        const records = payload?.data || payload?.users || [];
        setUsers(records);
      } catch (error) {
        console.error('Error fetching users:', error);
        toast.error('Unable to load users. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsers();
  }, [filters]);
  
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };
  
  const handleDeleteClick = (userId) => {
    const user = users.find(u => u.id === userId);
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    try {
      await usersAPI.delete(userToDelete.id);
      setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
      setShowModal(false);
      setShowDeleteModal(false);
      setUserToDelete(null);
      toast.success('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };
  
  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await usersAPI.update(userId, { is_active: newStatus === 'active' });
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, is_active: newStatus === 'active' } : u
      ));
    } catch (error) {
      console.error('Error updating user status:', error);
    }
  };
  
  const getRoleDisplayName = (role) => {
    const roleNames = {
      system_admin: 'System Administrator',
      regional_coordinator: 'Regional Coordinator',
      data_entry_officer: 'Data Entry Officer',
      program_officer: 'Program Officer',
      member: 'Member',
      super_admin: 'Super Administrator'
    };
    return roleNames[role] || role;
  };

  const handleOpenAddModal = () => {
    setAddForm(initialFormState);
    setShowAddModal(true);
  };

  const handleAddFormChange = (field, value) => {
    setAddForm(prev => ({ ...prev, [field]: value }));
  };

  const validateAddForm = () => {
    const errors = {};
    if (!addForm.first_name.trim()) errors.first_name = 'First name is required';
    if (!addForm.last_name.trim()) errors.last_name = 'Last name is required';
    if (!addForm.email.trim()) errors.email = 'Email is required';
    if (!addForm.role) errors.role = 'Role is required';
    return errors;
  };

  const handleAddUserSubmit = async (event) => {
    event.preventDefault();
    const errors = validateAddForm();
    if (Object.keys(errors).length) {
      toast.error(Object.values(errors)[0]);
      return;
    }

    setSavingUser(true);
    try {
      const response = await usersAPI.create(addForm);
      const createdUser = response.data?.user;
      if (createdUser) {
        setUsers(prev => [createdUser, ...prev]);
      }
      toast.success('User created successfully! Username and password have been sent to their email.');
      setShowAddModal(false);
      setAddForm(initialFormState);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create user';
      toast.error(message);
    } finally {
      setSavingUser(false);
    }
  };
  
  const columns = [
    {
      header: 'Name',
      accessor: 'first_name',
      render: (value, row) => `${row.first_name} ${row.last_name}`
    },
    {
      header: 'Email',
      accessor: 'email'
    },
    {
      header: 'Role',
      accessor: 'role',
      render: (value) => <RoleBadge theme={theme}>{getRoleDisplayName(value)}</RoleBadge>
    },
    {
      header: 'Region',
      accessor: 'region'
    },
    {
      header: 'Status',
      accessor: 'is_active',
      render: (value) => (
        <StatusBadge theme={theme} status={value ? 'active' : 'inactive'}>
          {value ? 'Active' : 'Inactive'}
        </StatusBadge>
      )
    },
    {
      header: 'Last Login',
      accessor: 'last_login',
      render: (value) => value ? new Date(value).toLocaleDateString() : 'Never'
    },
    {
      header: 'Actions',
      accessor: 'id',
      render: (value, row) => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button
            size="small"
            variant="ghost"
            onClick={() => handleUserClick(row)}
            leftIcon={<Eye size={14} />}
            leftIconTooltip="View User Details"
          >
            View
          </Button>
          {hasPermission('edit_users') && (
            <Button
              size="small"
              variant="ghost"
              onClick={() => handleUserClick(row)}
              leftIcon={<Edit size={14} />}
              leftIconTooltip="Edit User"
            >
              Edit
            </Button>
          )}
          {hasPermission('delete_users') && row.id !== user?.id && (
            <Button
              size="small"
              variant="ghost"
              onClick={() => handleDeleteClick(row.id)}
              leftIcon={<Trash2 size={14} />}
              leftIconTooltip="Delete User"
            >
              Delete
            </Button>
          )}
        </div>
      )
    }
  ];
  
  return (
    <UserManagementContainer theme={theme}>
      <Header theme={theme}>
        <Title theme={theme}>User Management</Title>
        {hasPermission('add_users') && (
          <Button 
            variant="primary" 
            leftIcon={<Plus size={16} />} 
            leftIconTooltip="Add New User"
            onClick={handleOpenAddModal}
          >
            Add User
          </Button>
        )}
      </Header>
      
      <FilterCard theme={theme}>
        <FilterRow theme={theme}>
          <FilterGroup theme={theme}>
            <FilterLabel theme={theme}>Search</FilterLabel>
            <Input
              placeholder="Search users..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              leftIcon={<Search size={16} />}
            />
          </FilterGroup>
          
          <FilterGroup theme={theme}>
            <FilterLabel theme={theme}>Role</FilterLabel>
            <Select
              theme={theme}
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
            >
              <option value="">All Roles</option>
              <option value="system_admin">System Administrator</option>
              <option value="regional_coordinator">Regional Coordinator</option>
              <option value="data_entry_officer">Data Entry Officer</option>
              <option value="program_officer">Program Officer</option>
              <option value="member">Member</option>
              <option value="super_admin">Super Administrator</option>
            </Select>
          </FilterGroup>
          
          <FilterGroup theme={theme}>
            <FilterLabel theme={theme}>Status</FilterLabel>
            <Select
              theme={theme}
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </FilterGroup>
        </FilterRow>
      </FilterCard>
      
      <Table
        columns={columns}
        data={users}
        loading={loading}
        onRowClick={handleUserClick}
        emptyMessage="No users found"
      />
      
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : ''}
        size="large"
      >
        {selectedUser && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <strong>Email:</strong> {selectedUser.email}
              </div>
              <div>
                <strong>Role:</strong> {getRoleDisplayName(selectedUser.role)}
              </div>
              <div>
                <strong>Region:</strong> {selectedUser.region}
              </div>
              <div>
                <strong>Status:</strong> {selectedUser.is_active ? 'Active' : 'Inactive'}
              </div>
              <div>
                <strong>Created:</strong> {new Date(selectedUser.created_at).toLocaleDateString()}
              </div>
              <div>
                <strong>Last Login:</strong> {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : 'Never'}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              {hasPermission('edit_users') && (
                <Button
                  variant={selectedUser.is_active ? "secondary" : "primary"}
                  onClick={() => handleToggleStatus(selectedUser.id, selectedUser.is_active ? 'active' : 'inactive')}
                  leftIcon={selectedUser.is_active ? <XCircle size={16} /> : <CheckCircle size={16} />}
                >
                  {selectedUser.is_active ? 'Deactivate' : 'Activate'}
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New User"
      >
        <form onSubmit={handleAddUserSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ 
            padding: '0.75rem 1rem', 
            backgroundColor: theme.colors.primary + '15', 
            border: `1px solid ${theme.colors.primary + '40'}`,
            borderRadius: theme.borderRadius.md,
            color: theme.colors.text.secondary,
            fontSize: theme.typography.fontSize.sm,
            marginBottom: '0.5rem'
          }}>
            <strong>Note:</strong> Username and password will be automatically generated and sent to the user's email address.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <Input
              label="First Name"
              value={addForm.first_name}
              onChange={(e) => handleAddFormChange('first_name', e.target.value)}
              required
            />
            <Input
              label="Last Name"
              value={addForm.last_name}
              onChange={(e) => handleAddFormChange('last_name', e.target.value)}
              required
            />
            <Input
              label="Email"
              type="email"
              value={addForm.email}
              onChange={(e) => handleAddFormChange('email', e.target.value)}
              required
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <FilterLabel theme={theme}>Role</FilterLabel>
              <Select
                theme={theme}
                value={addForm.role}
                onChange={(e) => handleAddFormChange('role', e.target.value)}
                required
              >
                <option value="">Select role</option>
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <FilterLabel theme={theme}>Status</FilterLabel>
              <Select
                theme={theme}
                value={addForm.is_active ? 'active' : 'inactive'}
                onChange={(e) => handleAddFormChange('is_active', e.target.value === 'active')}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <Button variant="outline" type="button" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={savingUser}>
              {savingUser ? 'Saving...' : 'Create User'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        title="Delete User"
      >
        <div style={{ padding: '1rem' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ 
              fontSize: theme.typography.fontSize.base,
              color: theme.colors.text.primary,
              marginBottom: '1rem'
            }}>
              Are you sure you want to delete this user? This action cannot be undone.
            </p>
            {userToDelete && (
              <div style={{
                padding: '1rem',
                backgroundColor: theme.colors.background.secondary,
                borderRadius: theme.borderRadius.md,
                border: `1px solid ${theme.colors.border.light}`
              }}>
                <p style={{ 
                  margin: 0,
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.semibold,
                  color: theme.colors.text.primary
                }}>
                  User Details:
                </p>
                <p style={{ 
                  margin: '0.5rem 0 0 0',
                  fontSize: theme.typography.fontSize.sm,
                  color: theme.colors.text.secondary
                }}>
                  <strong>Name:</strong> {userToDelete.first_name} {userToDelete.last_name}<br />
                  <strong>Email:</strong> {userToDelete.email}<br />
                  <strong>Role:</strong> {getRoleDisplayName(userToDelete.role)}
                </p>
              </div>
            )}
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '1rem',
            paddingTop: '1rem',
            borderTop: `1px solid ${theme.colors.border.light}`
          }}>
            <Button 
              variant="outline" 
              onClick={handleDeleteCancel}
            >
              Cancel
            </Button>
            <Button 
              variant="primary"
              onClick={handleDeleteConfirm}
              style={{ 
                backgroundColor: theme.colors.error,
                borderColor: theme.colors.error
              }}
            >
              Delete User
            </Button>
          </div>
        </div>
      </Modal>
    </UserManagementContainer>
  );
};

export default UserManagement;
